import { createContext, useContext, useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { Paragraph1, Paragraph2 } from 'inpock-ui-react';

import { useOutsideClick } from '@/hooks';

import { Backdrop } from '../Backdrop';

import * as S from './Modal.styled';
import { MODAL_TYPES, ModalProps, ModalTriggerProps } from './Modal.types';

const ModalContext = createContext<ModalProps>({} as ModalProps);

function ModalTrigger(props: ModalTriggerProps) {
  const { children, onClickShowModal } = props;

  const onClickModalTrigger = () => {
    onClickShowModal();
  };

  return <div onClick={onClickModalTrigger}>{children}</div>;
}

function ModalTitle() {
  const { type, title, subTitle, titleAlign, onCloseModal } = useContext(ModalContext);

  return (
    <S.ModalTitle
      className={type}
      titleAlign={titleAlign ? titleAlign : type === MODAL_TYPES.CONFIRM ? 'center' : 'left'}
    >
      <>
        {type === MODAL_TYPES.CONFIRM ? (
          <Paragraph1 className="modal-title">{title as string}</Paragraph1>
        ) : typeof title === 'string' ? (
          <Paragraph1 fontWeight="regular">{title}</Paragraph1>
        ) : (
          title
        )}
        {type === MODAL_TYPES.CLOSE && <S.IconClose onClick={onCloseModal} />}
      </>
      {subTitle && <Paragraph2 className="text-gray-600 mt-4">{subTitle}</Paragraph2>}
    </S.ModalTitle>
  );
}

function ModalBody() {
  const { content } = useContext(ModalContext);

  return <S.ModalBody>{content}</S.ModalBody>;
}

function ModalFooter() {
  const { footer, onCloseModal } = useContext(ModalContext);

  return (
    <S.ModalFooter>
      {(footer?.left || footer?.right) && (
        <div className="modal-btn-container">
          {footer?.left && (
            <div className="btn-modal-footer left" onClick={() => onCloseModal && onCloseModal()}>
              {footer.left}
            </div>
          )}
          {footer?.right && <div className="btn-modal-footer right">{footer.right}</div>}
        </div>
      )}
      <S.ModalFooterGuide className="text-gray-600">{footer?.guide}</S.ModalFooterGuide>
    </S.ModalFooter>
  );
}

function ModalContent() {
  const { onCloseModal, footer, disableOutsideClick } = useContext(ModalContext);
  const modalContentRef = useRef<HTMLDivElement>(null);

  useOutsideClick(modalContentRef, () => {
    if (!disableOutsideClick && onCloseModal) onCloseModal();
  });

  return (
    <S.ModalWrapper ref={modalContentRef}>
      <ModalTitle />
      <ModalBody />
      {footer && <ModalFooter />}
    </S.ModalWrapper>
  );
}

/**
 * 페이지를 가리고 사용자에게 정보를 입력받거나, 단순 확인을 위한 모달 컴포넌트입니다.
 * children을 prop으로 넘길 경우, ModalTrigger 컴포넌트로 전달됩니다.
 * ModalTrigger 클릭 시 모달이 노출되며 모달 컨텐츠 자체는 portal을 통해 backdrop에 렌더링됩니다.
 *
 * children을 사용하는 방식 대신 외부에서 Modal 컴포넌트를 사용할 경우 `externalShow`, `onCloseModal`을 사용하여 모달을 제어할 수 있습니다.
 *
 * `type`이 confirm인 경우 `title`은 string, `type`이 close인 경우 title은 string | React.ReactNode으로 받습니다.
 *
 * @example
 * ```tsx
 * // Modal 구조
 * <Modal>
 *  <ModalTrigger />
 *  <ModalContent>
 *    <ModalHeader />
 *    <ModalBody />
 *    <ModalFooter />
 *  </ModalContent>
 * </Modal>
 * ```
 */
export function Modal(props: ModalProps) {
  const { children, onShow, onCloseModal, externalShow } = props;
  const [backdrop, setBackdrop] = useState<HTMLDivElement | null>(null);
  // modal의 노출 여부를 관리하는 state
  const [show, setShow] = useState(externalShow || false);

  const onClickShowModal = () => {
    setShow(true);
  };

  const onClickCloseModal = () => {
    if (onCloseModal) onCloseModal();
    setShow(false);
  };

  useEffect(() => {
    setBackdrop(document.getElementById('backdrop') as HTMLDivElement);
  }, []);

  useEffect(() => {
    if (show && onShow) onShow();
  }, [show, onShow]);

  useEffect(() => {
    if (externalShow !== undefined) {
      setShow(externalShow);
    }
  }, [externalShow]);

  return (
    <>
      {children && <ModalTrigger onClickShowModal={onClickShowModal}>{children}</ModalTrigger>}
      {show && (
        <ModalContext.Provider value={{ ...props, onCloseModal: onClickCloseModal }}>
          {backdrop &&
            createPortal(
              <Backdrop deep>
                <ModalContent />
              </Backdrop>,
              backdrop as HTMLDivElement,
            )}
        </ModalContext.Provider>
      )}
    </>
  );
}
